import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { fiscalRegimeApi } from '../services/api/endpoints/fiscalRegime';
import { ApiService } from '../services/api/ApiService';
import type {
  FiscalRegime,
  FiscalRegimeTransitionPreview,
} from '../types/fiscal-regime';

/**
 * Hook de gestion du régime fiscal.
 *
 * - `currentRegime` : régime fiscal courant (chargé depuis `/organization`)
 * - `preview(newRegime)` : interroge le backend pour voir si une transition
 *   serait autorisée et avec quelles conséquences
 * - `change(newRegime, reason?)` : applique la transition après validation
 */
export function useFiscalRegime() {
  const [currentRegime, setCurrentRegime] = useState<FiscalRegime | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // L'endpoint /organization renvoie l'org complète avec fiscalRegime
      const response = await ApiService.get<{ success: boolean; data: any }>(
        '/organization',
      );
      const payload =
        (response as any)?.data?.data ?? (response as any)?.data ?? response;
      const data = payload?.data ?? payload;
      const regime: FiscalRegime = (data?.fiscalRegime as FiscalRegime) ?? 'NORMAL';
      setCurrentRegime(regime);
    } catch (err: any) {
      setError(err?.message || 'Erreur lors du chargement du régime fiscal');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const preview = useCallback(
    async (newRegime: FiscalRegime): Promise<FiscalRegimeTransitionPreview | null> => {
      try {
        const response = await fiscalRegimeApi.preview(newRegime);
        const payload =
          (response as any)?.data?.data ??
          (response as any)?.data ??
          response;
        return (payload as FiscalRegimeTransitionPreview) ?? null;
      } catch (err: any) {
        toast.error(err?.message || "Aperçu impossible");
        return null;
      }
    },
    [],
  );

  const change = useCallback(
    async (newRegime: FiscalRegime, reason?: string): Promise<boolean> => {
      try {
        setSaving(true);
        const response = await fiscalRegimeApi.change({ newRegime, reason });
        const payload =
          (response as any)?.data?.data ??
          (response as any)?.data ??
          response;
        const newRegimeApplied: FiscalRegime =
          payload?.organization?.fiscalRegime ?? newRegime;
        setCurrentRegime(newRegimeApplied);
        toast.success(`Régime fiscal mis à jour : ${newRegimeApplied}`);
        return true;
      } catch (err: any) {
        toast.error(err?.message || "Transition de régime refusée");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  /** L'entreprise est-elle exonérée de TVA ? */
  const isVatExempt = currentRegime === 'SMT' || currentRegime === 'FORFAITAIRE';

  return {
    currentRegime,
    isVatExempt,
    loading,
    saving,
    error,
    reload,
    preview,
    change,
  };
}
